import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addXP, loadXP } from '../data/xp';
import { syncProgress } from '../api/public';
import ModuleSummary from '../components/ModuleSummary';
import { recordStart } from '../data/progress';

// ─── Data ────────────────────────────────────────────────────────────────────

type Category = 'identity' | 'location' | 'behavior' | 'social' | 'financial';

interface DataPoint {
  label: string;
  detail: string;
  category: Category;
}

interface Moment {
  id: number;
  time: string;
  icon: string;
  title: string;
  scene: string;
  action: string;
  dataPoints: DataPoint[];
  privacyAlt: {
    action: string;
    blocked: string[];
  };
}

const MOMENTS: Moment[] = [
  {
    id: 1,
    time: '7:02 AM',
    icon: '📱',
    title: 'Wake Up & Check Phone',
    scene: "Maya reaches for her phone before even getting out of bed. She scrolls through notifications, checks the weather, and opens Instagram.",
    action: 'Unlocked phone, opened 3 apps, scrolled for 4 minutes',
    dataPoints: [
      { label: 'Wake-up time logged', detail: 'Your phone recorded the exact moment you unlocked it. Apps use this to learn your sleep schedule and show ads at peak attention times.', category: 'behavior' },
      { label: 'Device fingerprint sent', detail: "Your phone's model, screen size, OS version, battery level, and installed fonts were sent to 7 ad networks — a unique fingerprint that identifies you without cookies.", category: 'identity' },
      { label: 'Location logged (home)', detail: 'GPS recorded your exact home address. Combined with time, this confirms where you live — even if you never entered your address.', category: 'location' },
    ],
    privacyAlt: {
      action: 'Use Do Not Disturb mode overnight. Disable background app refresh.',
      blocked: ['Wake-up time logging', 'Background data syncs'],
    },
  },
  {
    id: 2,
    time: '7:31 AM',
    icon: '🎧',
    title: 'Walk to School',
    scene: 'Maya puts on her headphones and walks to school. Her phone is in her pocket with Bluetooth on, and she passes a bus stop with a digital billboard.',
    action: 'Walked 1.2 km with phone in pocket, Bluetooth & Wi-Fi on',
    dataPoints: [
      { label: 'Bluetooth beacons detected', detail: "The billboard at the bus stop pinged Maya's Bluetooth to log that a device passed by. Advertisers use this to measure if people saw their ad.", category: 'location' },
      { label: 'Wi-Fi probe requests sent', detail: "Your phone shouted out the names of every Wi-Fi network it's ever connected to — home, Starbucks, hotel. Anyone listening can build your travel history.", category: 'identity' },
      { label: 'Step count & route mapped', detail: "The health app tracked your exact walking route, speed, and steps. This data is shared with 'wellness partners' per the app's privacy policy.", category: 'behavior' },
      { label: 'Music listening habits logged', detail: 'Every song, skip, replay, and pause was recorded. Streaming services sell listening data to mood-inference engines used by insurers and employers.', category: 'behavior' },
    ],
    privacyAlt: {
      action: 'Turn off Bluetooth & Wi-Fi when not actively using them. Use offline playlists.',
      blocked: ['Bluetooth beacon tracking', 'Wi-Fi probe requests', 'Real-time listening data'],
    },
  },
  {
    id: 3,
    time: '8:15 AM',
    icon: '🏫',
    title: 'Connect to School Wi-Fi',
    scene: "Maya arrives at school and her phone auto-connects to the school Wi-Fi network. She doesn't think about it — it just connects.",
    action: 'Auto-connected to SchoolNet-5G, browsed during homeroom',
    dataPoints: [
      { label: 'MAC address logged by school', detail: "The school's network recorded your device's unique MAC address, logging when you arrived, where you moved in the building, and when you left.", category: 'identity' },
      { label: 'All web traffic visible', detail: "On school Wi-Fi, the network admin can see every website you visit, every search query, and every app that connects to the internet — even in 'incognito' mode.", category: 'behavior' },
      { label: 'Device inventory updated', detail: "The school IT system logged your phone's model, OS, and installed apps. Some schools share this with EdTech vendors.", category: 'identity' },
    ],
    privacyAlt: {
      action: 'Use a VPN on school Wi-Fi. Enable Private Wi-Fi Address in your phone settings.',
      blocked: ['MAC address tracking', 'Browsing history visibility'],
    },
  },
  {
    id: 4,
    time: '10:45 AM',
    icon: '🔍',
    title: 'Search Something Embarrassing',
    scene: "During a break, Maya searches 'how to know if someone likes you' and 'do braces make you ugly'. She uses the school computer.",
    action: 'Searched personal questions on school Chromebook',
    dataPoints: [
      { label: 'Search queries logged permanently', detail: "Google stored both searches in Maya's account forever. The school's web filter also logged them. These searches are tied to her real name via her school login.", category: 'behavior' },
      { label: 'Interest profile updated', detail: "Google's ad profile now tags Maya as interested in 'relationships' and 'dental/orthodontic.' She'll see targeted ads across every Google service.", category: 'behavior' },
      { label: 'Predictive profile enriched', detail: "Data brokers now classify Maya in the 'Insecure Teen' segment — a real advertising category used to target vulnerable audiences with beauty and diet products.", category: 'social' },
    ],
    privacyAlt: {
      action: 'Use DuckDuckGo or Brave Search. Never search personal topics on a school device.',
      blocked: ['Search query logging', 'Interest profile updates'],
    },
  },
  {
    id: 5,
    time: '12:20 PM',
    icon: '🍕',
    title: 'Buy Lunch with Card',
    scene: 'Maya taps her debit card at the cafeteria. She buys a slice of pizza, a juice, and a cookie. $7.50 total.',
    action: 'Contactless payment, $7.50 at school cafeteria',
    dataPoints: [
      { label: 'Purchase metadata recorded', detail: "Your bank recorded what you bought, when, where, and for how much. Banks sell anonymized transaction data to analytics firms — but 're-identification' can de-anonymize it.", category: 'financial' },
      { label: 'Dietary habits inferred', detail: 'Purchase patterns reveal dietary habits, meal timing, and spending frequency. This data feeds consumer profiles used by food companies and health insurers.', category: 'behavior' },
      { label: 'Location + timestamp confirmed', detail: 'The payment confirms Maya was physically at school at 12:20 PM — adding a verified data point to her location history.', category: 'location' },
    ],
    privacyAlt: {
      action: 'Pay with cash when possible. Cash leaves no digital trace.',
      blocked: ['Purchase metadata', 'Dietary habit inference'],
    },
  },
  {
    id: 6,
    time: '3:05 PM',
    icon: '📸',
    title: 'Post on Social Media',
    scene: "After school, Maya takes a selfie with friends and posts it to her Instagram story with the location tag 'Riverside Park.'",
    action: 'Posted photo with location tag, 3 friends tagged',
    dataPoints: [
      { label: 'Facial biometrics extracted', detail: "Instagram's algorithm scanned all 4 faces in the photo, extracting biometric data (face geometry) to identify people — even those without accounts.", category: 'identity' },
      { label: 'Location broadcast publicly', detail: "The location tag tells everyone — including strangers — exactly where Maya is right now. Combined with her school hours, her daily routine is now mappable.", category: 'location' },
      { label: 'Social graph updated', detail: 'By tagging friends, Maya confirmed social connections. The platform now knows who she spends time with, how often, and where.', category: 'social' },
      { label: 'EXIF data embedded', detail: 'The photo file contains hidden metadata: GPS coordinates, camera model, timestamp, and even altitude. Anyone who downloads the image gets all of it.', category: 'identity' },
    ],
    privacyAlt: {
      action: "Strip EXIF data before posting. Never use location tags. Avoid tagging others without their consent.",
      blocked: ['Public location broadcast', 'EXIF data leakage', 'Non-consensual tagging'],
    },
  },
  {
    id: 7,
    time: '5:30 PM',
    icon: '🎮',
    title: "Play a Free Mobile Game",
    scene: "Maya downloads a free puzzle game. It asks for access to her contacts, microphone, and photo library 'to enhance the experience.' She taps 'Allow All.'",
    action: 'Installed free game, granted all 3 permissions',
    dataPoints: [
      { label: 'Entire contact list uploaded', detail: "The game now has the names, phone numbers, and email addresses of everyone in Maya's phone — her family, friends, teachers. It sells this to data brokers.", category: 'social' },
      { label: 'Microphone access activated', detail: "The game can now listen through Maya's microphone — even when she's not playing. Some apps use this to detect what TV shows you watch for ad targeting.", category: 'behavior' },
      { label: 'Photo library scanned', detail: 'The app scanned all photos for location data, faces, and text in screenshots. Your private photos are now a data source.', category: 'identity' },
      { label: 'Ad ID linked across apps', detail: "Your device's advertising ID was sent to 14 different ad networks, linking your game activity to your browsing, shopping, and social media profiles.", category: 'identity' },
    ],
    privacyAlt: {
      action: "Deny all permissions that aren't essential. A puzzle game doesn't need your contacts or microphone.",
      blocked: ['Contact list upload', 'Microphone surveillance', 'Photo library scanning'],
    },
  },
  {
    id: 8,
    time: '9:15 PM',
    icon: '🛏️',
    title: 'Bedtime Browsing',
    scene: "Maya lies in bed browsing TikTok for 45 minutes, then checks tomorrow's weather, and finally opens a homework help site that asks her to create an account with her school email.",
    action: 'Browsed TikTok 45 min, created account on homework site with school email',
    dataPoints: [
      { label: 'Engagement pattern mapped', detail: 'TikTok tracked every video Maya watched, how long she watched, what she skipped, what she rewatched, and when she paused. This builds one of the most detailed behavioral profiles of any platform.', category: 'behavior' },
      { label: 'Biometric behavior captured', detail: 'TikTok records scroll speed, tap pressure, and keystroke patterns — these are behavioral biometrics that can identify you even without logging in.', category: 'identity' },
      { label: 'School email linked to new service', detail: 'By signing up with her school email, Maya linked her real identity to a third-party service. Data breaches at homework sites have exposed millions of student records.', category: 'identity' },
      { label: 'Sleep time predicted', detail: 'Your last activity timestamp tells platforms when you go to sleep. This data point completes the daily activity pattern — wake time to sleep time.', category: 'behavior' },
    ],
    privacyAlt: {
      action: 'Set screen time limits. Never use your school or personal email for random signups — use an alias. Restrict TikTok data access in settings.',
      blocked: ['Behavioral biometric capture', 'Identity linking via school email'],
    },
  },
];

const CATEGORIES: Record<Category, { label: string; color: string; icon: string }> = {
  identity:  { label: 'Identity',  color: '#ff4757', icon: '🪪' },
  location:  { label: 'Location',  color: '#ffa502', icon: '📍' },
  behavior:  { label: 'Behavior',  color: '#3742fa', icon: '🖱️' },
  social:    { label: 'Social',    color: '#2ed573', icon: '👥' },
  financial: { label: 'Financial', color: '#a855f7', icon: '💳' },
};

const DOSSIER_INFERENCES = [
  { inference: 'Lives in suburban area, likely single-family home',              confidence: '94%', sources: 'GPS home location + property records' },
  { inference: 'Attends Riverside Secondary School',                             confidence: '99%', sources: 'Wi-Fi connection + school email + payment location' },
  { inference: 'Insecure about physical appearance',                             confidence: '87%', sources: 'Search queries + browsing patterns' },
  { inference: 'Close friends with at least 3 specific individuals',             confidence: '96%', sources: 'Photo tags + contact frequency + location overlap' },
  { inference: "Parents' estimated household income: $65K–$85K",                confidence: '72%', sources: 'Home location + spending patterns + device model' },
  { inference: 'Likely plays soccer or another sport',                           confidence: '68%', sources: 'Step count patterns + after-school location data' },
  { inference: 'Vulnerable to targeted beauty/diet advertising',                 confidence: '91%', sources: 'Search history + engagement patterns + age bracket' },
  { inference: 'Daily routine fully mapped: wake 7am, school 8–3, park 3–4, home by 5:30', confidence: '98%', sources: 'Aggregated location + activity timestamps' },
];

const MODULE_ID = 'phantom-footprint';
const XP_PER_MOMENT = 15;
const XP_COMPLETION_BONUS = 120;

// ─── Sub-components ──────────────────────────────────────────────────────────

function DataPointCard({ point, index }: { point: DataPoint; index: number }) {
  const cat = CATEGORIES[point.category];
  return (
    <div style={{
      animation: `slideUp 0.45s cubic-bezier(0.34,1.56,0.64,1) ${index * 0.12}s both`,
      background: 'rgba(15,23,42,0.8)',
      border: `1px solid ${cat.color}33`,
      borderLeft: `3px solid ${cat.color}`,
      borderRadius: 8,
      padding: '14px 16px',
      marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 14 }}>{cat.icon}</span>
        <span style={{ color: cat.color, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' as const }}>
          {cat.label}
        </span>
      </div>
      <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{point.label}</div>
      <div style={{ color: '#94a3b8', fontSize: 12.5, lineHeight: 1.55 }}>{point.detail}</div>
    </div>
  );
}

function MomentTimeline({ moments, currentId, completed, onSelect }: {
  moments: Moment[];
  currentId: number;
  completed: number[];
  onSelect: (id: number) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', padding: '12px 0', marginBottom: 20 }}>
      {moments.map((m, i) => {
        const done = completed.includes(m.id);
        const active = currentId === m.id;
        const available = i === 0 || completed.includes(moments[i - 1].id);
        return (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <button
              onClick={() => available && onSelect(m.id)}
              disabled={!available}
              title={m.title}
              style={{
                width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                border: active ? '2px solid #00f5d4' : done ? '2px solid #2ed573' : '2px solid #334155',
                background: active ? 'rgba(0,245,212,0.12)' : done ? 'rgba(46,213,115,0.1)' : 'rgba(30,41,59,0.6)',
                cursor: available ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, opacity: available ? 1 : 0.35,
                boxShadow: active ? '0 0 20px rgba(0,245,212,0.25)' : 'none',
                transition: 'all 0.3s ease', position: 'relative' as const,
              }}
            >
              {done ? '✓' : m.icon}
            </button>
            {i < moments.length - 1 && (
              <div style={{ width: 18, height: 2, background: done ? '#2ed57355' : '#334155', margin: '0 2px', flexShrink: 0 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function DossierView({ allData, totalPoints, onReplay }: {
  allData: DataPoint[];
  totalPoints: number;
  onReplay: () => void;
}) {
  const [revealIndex, setRevealIndex] = useState(-1);

  useEffect(() => {
    const t = setInterval(() => {
      setRevealIndex(p => {
        if (p >= DOSSIER_INFERENCES.length - 1) { clearInterval(t); return p; }
        return p + 1;
      });
    }, 380);
    return () => clearInterval(t);
  }, []);

  const catCounts: Record<string, number> = {};
  allData.forEach(dp => { catCounts[dp.category] = (catCounts[dp.category] || 0) + 1; });

  return (
    <div style={{ animation: 'fadeIn 0.6s ease' }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ fontSize: 11, color: '#ff4757', letterSpacing: '0.15em', textTransform: 'uppercase' as const, marginBottom: 8 }}>
          ⚠ CLASSIFIED · DATA BROKER FILE
        </div>
        <h2 style={{ fontSize: 26, color: '#f1f5f9', fontWeight: 700, margin: 0 }}>Subject: Maya Chen, Age 15</h2>
        <div style={{ color: '#64748b', fontSize: 13, marginTop: 6 }}>{totalPoints} data points collected in a single day</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(80px,1fr))', gap: 10, marginBottom: 24 }}>
        {(Object.entries(CATEGORIES) as [Category, typeof CATEGORIES[Category]][]).map(([key, cat]) => (
          <div key={key} style={{ background: `${cat.color}11`, border: `1px solid ${cat.color}33`, borderRadius: 8, padding: '12px 8px', textAlign: 'center' as const }}>
            <div style={{ fontSize: 20 }}>{cat.icon}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: cat.color }}>{catCounts[key] || 0}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{cat.label}</div>
          </div>
        ))}
      </div>

      <div style={{ background: 'rgba(255,71,87,0.06)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: '#ff4757', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: 14 }}>
          🧠 What data brokers inferred about Maya
        </div>
        {DOSSIER_INFERENCES.map((item, i) => (
          <div key={i} style={{
            opacity: i <= revealIndex ? 1 : 0,
            transform: i <= revealIndex ? 'translateX(0)' : 'translateX(-20px)',
            transition: `all 0.4s ease ${i * 0.05}s`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            padding: '10px 0',
            borderBottom: i < DOSSIER_INFERENCES.length - 1 ? '1px solid rgba(255,71,87,0.1)' : 'none',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#e2e8f0', fontSize: 13.5, fontWeight: 500 }}>{item.inference}</div>
              <div style={{ color: '#64748b', fontSize: 11, marginTop: 3 }}>Sources: {item.sources}</div>
            </div>
            <div style={{ color: parseInt(item.confidence) > 85 ? '#ff4757' : '#ffa502', fontSize: 13, fontWeight: 700, marginLeft: 12, flexShrink: 0 }}>
              {item.confidence}
            </div>
          </div>
        ))}
      </div>

      <button onClick={onReplay} style={btnGreen}>
        👻 Replay with Privacy Mode →
      </button>
    </div>
  );
}

function ReplayView({ totalBefore }: { totalBefore: number }) {
  const totalBlocked = MOMENTS.reduce((a, m) => a + m.privacyAlt.blocked.length, 0);
  const remaining = Math.max(totalBefore - totalBlocked, 3);
  const [showAfter, setShowAfter] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setShowAfter(true), 700);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{ animation: 'fadeIn 0.6s ease' }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: '#00f5d4', letterSpacing: '0.15em', textTransform: 'uppercase' as const, marginBottom: 8 }}>🛡️ PRIVACY MODE RESULTS</div>
        <h2 style={{ fontSize: 24, color: '#f1f5f9', fontWeight: 700, margin: 0 }}>Before vs. After</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
        <div style={{ background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.25)', borderRadius: 12, padding: 20, textAlign: 'center' as const }}>
          <div style={{ fontSize: 11, color: '#ff4757', fontWeight: 700, textTransform: 'uppercase' as const, marginBottom: 12 }}>Without Protection</div>
          <div style={{ fontSize: 40, fontWeight: 700, color: '#ff4757' }}>{totalBefore}</div>
          <div style={{ color: '#94a3b8', fontSize: 12 }}>data points collected</div>
        </div>
        <div style={{
          background: showAfter ? 'rgba(0,245,212,0.08)' : 'rgba(30,41,59,0.5)',
          border: `1px solid ${showAfter ? 'rgba(0,245,212,0.25)' : '#334155'}`,
          borderRadius: 12, padding: 20, textAlign: 'center' as const, transition: 'all 0.8s ease',
        }}>
          <div style={{ fontSize: 11, color: '#00f5d4', fontWeight: 700, textTransform: 'uppercase' as const, marginBottom: 12 }}>With Privacy Mode</div>
          <div style={{ fontSize: 40, fontWeight: 700, color: '#00f5d4', opacity: showAfter ? 1 : 0, transition: 'opacity 0.8s ease' }}>{remaining}</div>
          <div style={{ color: '#94a3b8', fontSize: 12 }}>data points collected</div>
        </div>
      </div>

      <div style={{ background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.15)', borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: '#00f5d4', fontWeight: 700, textTransform: 'uppercase' as const, marginBottom: 14 }}>What you blocked at each step</div>
        {MOMENTS.map((m, i) => (
          <div key={m.id} style={{ padding: '10px 0', borderBottom: i < MOMENTS.length - 1 ? '1px solid rgba(0,245,212,0.08)' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 15 }}>{m.icon}</span>
              <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>{m.title}</span>
            </div>
            <div style={{ color: '#64748b', fontSize: 12, fontStyle: 'italic', marginBottom: 4 }}>→ {m.privacyAlt.action}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4 }}>
              {m.privacyAlt.blocked.map((b, j) => (
                <span key={j} style={{ background: 'rgba(0,245,212,0.12)', color: '#00f5d4', fontSize: 10.5, padding: '3px 8px', borderRadius: 4 }}>
                  🚫 {b}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: 'rgba(46,213,115,0.08)', border: '1px solid rgba(46,213,115,0.2)', borderRadius: 12, padding: 24, textAlign: 'center' as const, marginBottom: 16 }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>👻</div>
        <div style={{ color: '#2ed573', fontSize: 18, fontWeight: 700 }}>Ghost Walker Badge Earned!</div>
        <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>
          You reduced Maya's data exposure by {Math.round(((totalBefore - remaining) / totalBefore) * 100)}%
        </div>
        <div style={{ color: '#ffa502', fontSize: 14, fontWeight: 700, marginTop: 10 }}>
          +{XP_COMPLETION_BONUS} XP earned
        </div>
      </div>

      <button onClick={() => navigate('/')} style={btnGreen}>
        ← Back to Dashboard
      </button>
    </div>
  );
}

// ─── Shared button styles ─────────────────────────────────────────────────────

const btnGreen: React.CSSProperties = {
  width: '100%', padding: 16, borderRadius: 12,
  background: 'linear-gradient(135deg,#00f5d4,#00b894)',
  border: 'none', color: '#0f172a', fontSize: 15, fontWeight: 700,
  cursor: 'pointer',
};

// ─── Main page ────────────────────────────────────────────────────────────────

type Screen = 'intro' | 'playing' | 'dossier' | 'replay';

export default function PhantomFootprint() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>('intro');
  const [currentId, setCurrentId] = useState(1);
  const [completed, setCompleted] = useState<number[]>([]);
  const [revealed, setRevealed] = useState(false);
  const [dataCounter, setDataCounter] = useState(0);
  const [allData, setAllData] = useState<DataPoint[]>([]);
  const [sessionXP, setSessionXP] = useState(0);

  const moment = MOMENTS.find(m => m.id === currentId)!;

  function handleReveal() {
    setRevealed(true);
    setDataCounter(p => p + moment.dataPoints.length);
    setAllData(p => [...p, ...moment.dataPoints]);
    const updated = addXP(XP_PER_MOMENT);
    setSessionXP(p => p + XP_PER_MOMENT);
    syncProgress(MODULE_ID, updated.total);
  }

  function handleNext() {
    const nowCompleted = [...completed, currentId];
    setCompleted(nowCompleted);
    setRevealed(false);
    if (currentId < MOMENTS.length) {
      setCurrentId(currentId + 1);
    } else {
      // award completion bonus (idempotent via moduleId)
      addXP(XP_COMPLETION_BONUS, MODULE_ID);
      recordStart();
      setSessionXP(p => p + XP_COMPLETION_BONUS);
      syncProgress(MODULE_ID, loadXP().total);
      setScreen('dossier');
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0e1a',
      backgroundImage: 'radial-gradient(ellipse at 20% 50%, rgba(0,245,212,0.04) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(55,66,250,0.04) 0%, transparent 60%)',
      color: '#e2e8f0',
      paddingTop: 64, // below sticky nav
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(20px) scale(0.96); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.45; } }
      `}</style>

      <div style={{ maxWidth: 540, margin: '0 auto', padding: '20px 16px 48px' }}>

        {/* Header bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 10, color: '#00f5d4', letterSpacing: '0.15em', textTransform: 'uppercase' as const }}>
              SIMULATION · INVESTIGATIVE
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f8fafc', lineHeight: 1.2, marginTop: 2 }}>
              👻 Phantom Footprint
            </h1>
          </div>
          <div style={{ textAlign: 'right' as const }}>
            <div style={{ fontSize: 11, color: '#64748b' }}>DATA COLLECTED</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: dataCounter > 15 ? '#ff4757' : dataCounter > 8 ? '#ffa502' : '#00f5d4', transition: 'color 0.3s' }}>
              {dataCounter}
            </div>
            <div style={{ fontSize: 10, color: '#ffa502' }}>⭐ {sessionXP} XP</div>
          </div>
        </div>

        {/* ── Intro ── */}
        {screen === 'intro' && (
          <div style={{ animation: 'fadeIn 0.6s ease' }}>
            <div style={{
              background: 'linear-gradient(135deg,rgba(0,245,212,0.08),rgba(55,66,250,0.08))',
              border: '1px solid rgba(0,245,212,0.15)',
              borderRadius: 16, padding: '28px 24px', marginBottom: 24, position: 'relative' as const, overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg,transparent,#00f5d4,transparent)', animation: 'pulse 2s infinite' }} />
              <div style={{ fontSize: 48, marginBottom: 16 }}>👻</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>Trace your invisible trail</h2>
              <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, marginBottom: 8 }}>
                Meet <strong style={{ color: '#e2e8f0' }}>Maya Chen, age 15</strong>. You're about to live through her ordinary day — checking her phone, walking to school, posting on social media, playing games.
              </p>
              <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, marginBottom: 8 }}>
                After each moment, we'll reveal the <strong style={{ color: '#ff4757' }}>invisible data</strong> silently collected. By the end of the day, you'll see exactly what a data broker knows about Maya.
              </p>
              <p style={{ fontSize: 14, color: '#00f5d4', lineHeight: 1.7 }}>
                Then — you'll replay the day and try to erase her footprint.
              </p>
            </div>
            <button onClick={() => setScreen('playing')} style={btnGreen}>Start Maya's Day →</button>
            <button onClick={() => navigate('/')} style={{ width: '100%', marginTop: 10, padding: '12px', background: 'none', border: '1px solid #334155', borderRadius: 10, color: '#64748b', fontSize: 14, cursor: 'pointer' }}>
              ← Back to Dashboard
            </button>
          </div>
        )}

        {/* ── Playing ── */}
        {screen === 'playing' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <MomentTimeline
              moments={MOMENTS}
              currentId={currentId}
              completed={completed}
              onSelect={id => {
                if (completed.includes(id)) { setCurrentId(id); setRevealed(true); }
              }}
            />

            {/* Moment card */}
            <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 16, padding: '24px 20px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 28 }}>{moment.icon}</span>
                <div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{moment.time}</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9' }}>{moment.title}</div>
                </div>
              </div>
              <p style={{ fontSize: 13.5, color: '#cbd5e1', lineHeight: 1.65, marginBottom: 12 }}>{moment.scene}</p>
              <div style={{ background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.12)', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#00f5d4' }}>
                → {moment.action}
              </div>
            </div>

            {!revealed && !completed.includes(currentId) ? (
              <button
                onClick={handleReveal}
                style={{ width: '100%', padding: 14, borderRadius: 10, background: 'rgba(255,71,87,0.15)', border: '1px solid rgba(255,71,87,0.3)', color: '#ff4757', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              >
                🔍 Reveal what was collected →
              </button>
            ) : (
              <div style={{ animation: 'fadeIn 0.3s ease' }}>
                <div style={{ fontSize: 11, color: '#ff4757', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ animation: 'pulse 1s infinite', display: 'inline-block' }}>⬤</span> DATA SILENTLY COLLECTED
                </div>
                {moment.dataPoints.map((dp, i) => (
                  <DataPointCard key={i} point={dp} index={i} />
                ))}
                {!completed.includes(currentId) && (
                  <button
                    onClick={handleNext}
                    style={{
                      width: '100%', padding: 14, borderRadius: 10, marginTop: 12,
                      background: currentId < MOMENTS.length
                        ? 'linear-gradient(135deg,rgba(55,66,250,0.3),rgba(55,66,250,0.15))'
                        : 'linear-gradient(135deg,#ff4757,#ff6b81)',
                      border: currentId < MOMENTS.length ? '1px solid rgba(55,66,250,0.3)' : 'none',
                      color: '#f1f5f9', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {currentId < MOMENTS.length ? `Continue to ${MOMENTS[currentId].time} →` : 'View Full Dossier →'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Dossier ── */}
        {screen === 'dossier' && (
          <>
            <DossierView
              allData={allData}
              totalPoints={dataCounter}
              onReplay={() => setScreen('replay')}
            />
            <ModuleSummary moduleId="phantom" xpEarned={sessionXP} />
          </>
        )}

        {/* ── Replay ── */}
        {screen === 'replay' && (
          <ReplayView totalBefore={dataCounter} />
        )}

      </div>
    </div>
  );
}
